import type { Move } from '../types/move';
import {
  MOVE_DAMAGE_CLASS_PHYSICAL,
  MOVE_DAMAGE_CLASS_SPECIAL,
  MOVE_DAMAGE_CLASS_STATUS,
} from '../types/move';

type MoveLike = Pick<Move, 'damage_class_number'> | null | undefined;
type MoveNameLike = Pick<Move, 'name_ja'> | null | undefined;
type MoveKabehakaiLike = Pick<Move, 'name_ja' | 'id'> | null | undefined;
type MoveIdLike = Pick<Move, 'id'> | null | undefined;
type MoveNameAndClassLike = Pick<Move, 'name_ja' | 'damage_class_number'> | null | undefined;

// 技の性質判定は名称ベースで行うため、対象をSetでまとめておく
const WRING_OUT_MOVE_NAMES = new Set(['しぼりとる', 'にぎりつぶす']);
// にぎりつぶす/ハードプレスをまとめて判定したいケース用
const CRUSH_GRIP_OR_HARD_PRESS_MOVE_NAMES = new Set(['にぎりつぶす', 'ハードプレス']);
const HAKIDASU_MOVE_NAMES = new Set(['はきだす']);
const ZIDANDA_MOVE_NAMES = new Set(['じだんだ', 'やけっぱち']);
const KARAGENKI_MOVE_NAMES = new Set(['からげんき']);
const ZISHIN_MOVE_NAMES = new Set(['じしん']);
const ELECTRO_BALL_MOVE_NAMES = new Set(['エレキボール']);
const GYRO_BALL_MOVE_NAMES = new Set(['ジャイロボール']);
const REVELATION_DANCE_MOVE_NAMES = new Set(['めざめるダンス']);
const TECHNO_BLAST_MOVE_NAMES = new Set(['テクノバスター']);
const MULTI_ATTACK_MOVE_NAMES = new Set(['マルチアタック']);
const FREEZE_DRY_MOVE_NAMES = new Set(['フリーズドライ']);
const WEATHER_BALL_MOVE_NAMES = new Set(['ウェザーボール']);
const BODY_PRESS_MOVE_NAMES = new Set(['ボディプレス']);
const FLYING_PRESS_MOVE_NAMES = new Set(['フライングプレス']);
const KISHIKAISEI_MOVE_NAMES = new Set(['きしかいせい', 'じたばた']);
const MAGNITUDE_MOVE_NAMES = new Set(['マグニチュード']);
const IKASAMA_MOVE_NAMES = new Set(['イカサマ']);
const STORED_POWER_MOVE_NAMES = new Set(['アシストパワー']);
const RISING_BOLT_MOVE_NAMES = new Set(['ライジングボルト']);
const TRIPLE_KICK_ACCELERATOR_MOVE_NAMES = new Set(['トリプルキック', 'トリプルアクセル']);
const HEAVY_SLAM_MOVE_NAMES = new Set(['ヘビーボンバー', 'ヒートスタンプ']);
const HATAKIOTOSU_MOVE_NAMES = new Set(['はたきおとす']);
const OHAKAMAIRI_MOVE_NAMES = new Set(['おはかまいり']);
const KIRU_MOVE_NAMES = new Set([
  'アクアカッター',
  'いあいぎり',
  'エアカッター',
  'エアスラッシュ',
  'がんせきアックス',
  'きょじゅうざん',
  'きりさく',
  'クロスポイズン',
  'サイコカッター',
  'サイコブレイド',
  'シェルブレード',
  'シザークロス',
  'しんぴのつるぎ',
  'せいなるつるぎ',
  'ソーラーブレード',
  'タキオンカッター',
  'つじぎり',
  'つばめがえし',
  'ドゲザン',
  'ネズミざん',
  'はっぱカッター',
  'パワフルエッジ',
  'ひけん・ちえなみ',
  'むねんのつるぎ',
  'リーフブレード',
  'れんぞくぎり',
]);
const KAKUTEI_CRITICAL_MOVE_NAMES = new Set([
  'やまあらし',
  'こおりのいぶき',
  'ばちばちアクセル',
  'あんこくきょうだ',
  'すいりゅうれんだ',
  'トリックフラワー',
]);
const KATAYABURI_MOVE_NAMES = new Set(['メテオドライブ', 'シャドーレイ', 'フォトンゲイザー']);
const THROUGH_RANK_MOVE_NAMES = new Set(['なしくずし', 'せいなるつるぎ', 'DDラリアット']);
const PHOTON_GEYSER_MOVE_NAMES = new Set(['フォトンゲイザー']);
const PSYSHOCK_MOVE_NAMES = new Set(['サイコショック', 'サイコブレイク', 'しんぴのつるぎ']);
const SUTEMI_WAZA_MOVE_NAMES = new Set([
  'アフロブレイク',
  'ウェーブタックル',
  'ウッドハンマー',
  'かかとおとし',
  'サンダーダイブ',
  'じごくぐるま',
  'すてみタックル',
  'とっしん',
  'とびげり',
  'とびひざげり',
  'はめつのひかり',
  'フレアドライブ',
  'ブレイブバード',
  'ボルテッカー',
  'もろはのずつき',
  'ワイルドボルト',
]);
const KAMITUKI_MOVE_NAMES = new Set([
  'エラがみ',
  'かみくだく',
  'かみつく',
  'かみなりのキバ',
  'くらいつく',
  'こおりのキバ',
  'サイコファング',
  'どくどくのキバ',
  'ひっさつまえば',
  'ほのおのキバ',
]);
const KAZE_MOVE_NAMES = new Set([
  'エアカッター',
  'エアロブラスト',
  'おいかぜ',
  'かぜおこし',
  'かみなりあらし',
  'こがらしあらし',
  'こごえるかぜ',
  'すなあらし',
  'たつまき',
  'ねっさのあらし',
  'ねっぷう',
  'はなふぶき',
  'はるのあらし',
  'ふきとばし',
  'ふぶき',
  'ぼうふう',
  'ようせいのかぜ',
]);
const OTO_MOVE_NAMES = new Set([
  'いにしえのうた',
  'いびき',
  'いやしのすず',
  'いやなおと',
  'うたう',
  'うたかたのアリア',
  'エコーボイス',
  'オーバードライブ',
  'おしゃべり',
  'おたけび',
  'きんぞくおん',
  'くさぶえ',
  'サイコノイズ',
  'さわぐ',
  'スケイルノイズ',
  'すてゼリフ',
  'ソウルビート',
  'ダークパニック',
  'チャームボイス',
  'ちょうおんぱ',
  'とおぼえ',
  'ないしょばなし',
  'なきごえ',
  'バークアウト',
  'ハイパーボイス',
  'ばくおんぱ',
  'ぶきみなじゅもん',
  'フレアソング',
  'ブレイジングソウルビート',
  'ほえる',
  'ほろびのうた',
  'みわくのボイス',
  'むしのさざめき',
  'りんしょう',
]);
const KABEHARAKAI_MOVE_NAMES = new Set(['かわらわり', 'サイコファング', 'レイジングブル']);
const KABEHARAKAI_MOVE_IDS = new Set([280, 706, 873]);
const KETAGURI_MOVE_NAMES = new Set(['けたぐり', 'くさむすび']);
// UI入力（double_power_condition）で威力2倍を指定できる技
// - しおみず(362): 相手HPが半分以下
// - しっぺがえし(371): 行動順で後攻
// - ゆきなだれ(419): 先にダメージを受けた
// - ベノムショック(474): 相手がどく/もうどく（UIでは自動判定のため手動2倍は非表示）
// - たたりめ(506): 相手が状態異常（UIでは自動判定のため手動2倍は非表示）
// - アクロバット(512): 2倍チェック時（ポケソルテキスト互換のため道具有無では判定しない）
// - かたきうち(514): 味方が倒された直後
// - クロスフレイム(558): クロスサンダー直後
// - クロスサンダー(559): クロスフレイム直後
// - じだんだ(707): 直前に技を外す/失敗
// - でんげきくちばし(754): 先に行動
// - エラがみ(755): 先に行動
// - どくばりセンボン(839): 相手がどく/もうどく（UIでは自動判定のため手動2倍は非表示）
// - ひゃっきやこう(844): 相手が状態異常（UIでは自動判定のため手動2倍は非表示）
// - きまぐレーザー(907): 30%で2倍（UIで手動指定）
// - やけっぱち(915): 直前に技を外す/失敗
// - ちいさくなる状態の相手に対する2倍（第9世代のみ反映）:
//   ふみつけ(23), のしかかり(34), ドラゴンダイブ(407), ヘビーボンバー(484),
//   ヒートスタンプ(535), フライングプレス(560), サンダーダイブ(916)
const DOUBLE_POWER_MOVE_IDS = new Set([
  362, 371, 419, 474, 506, 512, 514, 558, 559, 707, 754, 755, 839, 844, 907, 915,
  23, 34, 407, 484, 535, 560, 916,
]);
const HADOU_MOVE_NAMES = new Set([
  'あくのはどう',
  'いやしのはどう',
  'こんげんのはどう',
  'だいちのはどう',
  'はどうだん',
  'みずのはどう',
  'りゅうのはどう',
]);
const WAVES_OF_THE_EARTH_MOVE_NAMES = new Set(['だいちのはどう']);
const TERABRAST_MOVE_NAMES = new Set(['テラバースト']);
const TERAS_POWER_UP_MOVE_NAMES = new Set(['テラバースト', 'テラクラスター']);
const SHELL_SIDE_ARM_MOVE_NAMES = new Set(['シェルアームズ']);
const NAMAKE_MOVE_NAMES = new Set(['なまける']);
const HYDRO_STEAM_MOVE_NAMES = new Set(['ハイドロスチーム']);
const RENZOKU_MOVE_NAMES = new Set([
  'ダブルニードル',
  'にどげり',
  'ホネブーメラン',
  'ダブルアタック',
  'ギアソーサー',
  'ダブルチョップ',
  'ダブルパンツァー',
  'ドラゴンアロー',
  'ダブルウイング',
  'タキオンカッター',
  'ツインビーム',
  'すいりゅうれんだ',
  'トリプルダイブ',
  'おうふくビンタ',
  'たまなげ',
  'とげキャノン',
  'ミサイルばり',
  'みだれづき',
  'みだれひっかき',
  'れんぞくパンチ',
  'ボーンラッシュ',
  'タネマシンガン',
  'つっぱり',
  'つららばり',
  'ロックブラスト',
  'スイープビンタ',
  'みずしゅりけん',
  'スケイルショット',
  'トリプルキック',
  'トリプルアクセル',
  'ネズミざん',
]);
const MAKIBISI_MOVE_NAMES = new Set(['まきびし']);
const WIDE_FORCE_MOVE_NAMES = new Set(['ワイドフォース']);
const PSY_BLADE_MOVE_NAMES = new Set(['サイコブレイド']);
const TAMA_MOVE_NAMES = new Set([
  'アイスボール',
  'アシッドボム',
  'ウェザーボール',
  'エナジーボール',
  'エレキボール',
  'オクタンほう',
  'かえんだん',
  'かえんボール',
  'かふんだんご',
  'がんせきほう',
  'きあいだま',
  'くちばしキャノン',
  'ジャイロボール',
  'シャドーボール',
  'タネばくだん',
  'タネマシンガン',
  'タマゴばくだん',
  'たまなげ',
  'でんじほう',
  'どろばくだん',
  'はどうだん',
  'ヘドロばくだん',
  'マグネットボム',
  'みずあめボム',
  'ミストボール',
  'ロックブラスト',
]);
const PUNCH_MOVE_NAMES = new Set([
  'アームハンマー',
  'アイスハンマー',
  'あんこくきょうだ',
  'かみなりパンチ',
  'きあいパンチ',
  'グロウパンチ',
  'コメットパンチ',
  'ジェットパンチ',
  'シャドーパンチ',
  'すいりゅうれんだ',
  'スカイアッパー',
  'ダブルパンツァー',
  'ドレインパンチ',
  'ばくれつパンチ',
  'バレットパンチ',
  'ピヨピヨパンチ',
  'ぶちかまし',
  'プラズマフィスト',
  'ふんどのこぶし',
  'ほのおのパンチ',
  'マッハパンチ',
  'メガトンパンチ',
  'れいとうパンチ',
  'れんぞくパンチ',
]);
const TIKARAZUKU_TARGET_MOVE_NAMES = new Set([
  'バークアウト',
  'ナイトバースト',
  'かみつく',
  'かみくだく',
  'あくのはどう',
  'げんしのちから',
  'がんせきふうじ',
  'いわなだれ',
  'ねんりき',
  'じんつうりき',
  'しねんのずつき',
  'サイコキネシス',
  'サイケこうせん',
  'ローキック',
  'はっけい',
  'ばくれつパンチ',
  'けたぐり',
  'きあいだま',
  'いわくだき',
  'りんごさん',
  'トロピカルキック',
  'エナジーボール',
  'Gのちから',
  'シャドーボール',
  'おどろかす',
  'れいとうビーム',
  'れいとうパンチ',
  'フリーズドライ',
  'ふぶき',
  'つららおとし',
  'こごえるかぜ',
  'こおりのキバ',
  'オーロラビーム',
  'マッドショット',
  'どろかけ',
  'だいちのちから',
  'じならし',
  'ほっぺすりすり',
  'ほうでん',
  'びりびりちくちく',
  'でんじほう',
  'でんきショック',
  'チャージビーム',
  'スパーク',
  'かみなりパンチ',
  'かみなりのキバ',
  'かみなり',
  'エレキネット',
  '10まんボルト',
  'ようかいえき',
  'ポイズンテール',
  'ヘドロばくだん',
  'ヘドロこうげき',
  'ヘドロウェーブ',
  'どくばり',
  'どくどくのキバ',
  'どくづき',
  'ダストシュート',
  'スモッグ',
  'クロスポイズン',
  'ワイドブレイカー',
  'りゅうのいぶき',
  'ドラゴンダイブ',
  'たつまき',
  'ブレイククロー',
  'ふみつけ',
  'のしかかり',
  'ねこだまし',
  'トライアタック',
  'ずつき',
  'こうそくスピン',
  'いびき',
  'ラスターカノン',
  'メタルクロー',
  'はがねのつばさ',
  'コメットパンチ',
  'アイアンヘッド',
  'アイアンテール',
  'とびはねる',
  'ゴッドバード',
  'エアスラッシュ',
  'ムーンフォース',
  'ソウルクラッシュ',
  'じゃれつく',
  'れんごく',
  'マジカルフレイム',
  'ほのおのムチ',
  'ほのおのまい',
  'ほのおのパンチ',
  'ほのおのキバ',
  'ふんえん',
  'ひのこ',
  'ねっぷう',
  'だいもんじ',
  'かえんほうしゃ',
  'みずのはどう',
  'バブルこうせん',
  'だくりゅう',
  'たきのぼり',
  'シェルブレード',
  'アクアブレイク',
  'むしのていこう',
  'むしのさざめき',
  'はいよるいちげき',
  'とびかかる',
]);
const PHYSICAL_NON_DIRECT_MOVE_NAMES = new Set([
  '3ぼんのや',
  'Gのちから',
  'アクアカッター',
  'いじげんラッシュ',
  'いっちょうあがり',
  'いわおとし',
  'いわなだれ',
  'うちおとす',
  'オーラぐるま',
  'おはかまいり',
  'かえんボール',
  'かげぬい',
  'がんせきふうじ',
  'がんせきほう',
  'グランドフォース',
  'クロスサンダー',
  'こうげきしれい',
  'こおりのつぶて',
  'ゴッドバード',
  'このは',
  'サイコカッター',
  'サウザンアロー',
  'サウザンウェーブ',
  'しおづけ',
  'じしん',
  'しぜんのめぐみ',
  'じならし',
  'じばく',
  'シャドーボーン',
  'じわれ',
  'スケイルショット',
  'スターアサルト',
  'ストーンエッジ',
  'すなじごく',
  'せいなるほのお',
  'ダークアクセル',
  'だいばくはつ',
  'だいふんげき',
  'ダイヤストーム',
  'ダストシュート',
  'タネばくだん',
  'タネマシンガン',
  'ダブルニードル',
  'タマゴばくだん',
  'たまなげ',
  'だんがいのつるぎ',
  'ツタこんぼう',
  'つららおとし',
  'つららばり',
  'デカハンマー',
  'どくばり',
  'どくばりセンボン',
  'とげキャノン',
  'ドラゴンアロー',
  'ドラムアタック',
  'トリックフラワー',
  'なげつける',
  'ネコにこばん',
  'バーンアクセル',
]);
const SPECIAL_NON_DIRECT_MOVE_NAMES = new Set([
  'うたかたのアリア',
  'ソウルクラッシュ',
  'チャームボイス',
  'ハイパーボイス',
  'みわくのボイス',
  'むしのさざめき',
  'りんしょう',
  'エコーボイス',
  'ばくおんぱ',
  'フレアソング',
]);
const HUNDONOKOBUSHI_MOVE_NAMES = new Set(['ふんどのこぶし']);

const getDamageClass = (move: MoveLike) => move?.damage_class_number ?? null;

export const isPhysicalMove = (move: MoveLike): boolean =>
  getDamageClass(move) === MOVE_DAMAGE_CLASS_PHYSICAL;

export const isSpecialMove = (move: MoveLike): boolean =>
  getDamageClass(move) === MOVE_DAMAGE_CLASS_SPECIAL;

export const isStatusMove = (move: MoveLike): boolean =>
  getDamageClass(move) === MOVE_DAMAGE_CLASS_STATUS;

export const isDamagingMove = (move: MoveLike): boolean =>
  isPhysicalMove(move) || isSpecialMove(move);

export const isWringOutMove = (move: MoveNameLike): boolean =>
  WRING_OUT_MOVE_NAMES.has(move?.name_ja ?? '');

// にぎりつぶす/ハードプレスを判定する
export const isCrushGripOrHardPressMove = (move: MoveNameLike): boolean =>
  CRUSH_GRIP_OR_HARD_PRESS_MOVE_NAMES.has(move?.name_ja ?? '');

// はきだすは名称でも判定できるようにする
export const isHakidasuMove = (move: MoveNameLike): boolean =>
  HAKIDASU_MOVE_NAMES.has(move?.name_ja ?? '');

export const isZidandaMove = (move: MoveNameLike): boolean =>
  ZIDANDA_MOVE_NAMES.has(move?.name_ja ?? '');

// からげんきは名称でも判定できるようにする
export const isKaragenkiMove = (move: MoveNameLike): boolean =>
  KARAGENKI_MOVE_NAMES.has(move?.name_ja ?? '');

// 威力2倍条件の対象技をIDで判定する
export const isDoublePowerMoveId = (moveId: number | null | undefined): boolean =>
  DOUBLE_POWER_MOVE_IDS.has(moveId ?? -1);

// 威力2倍条件の対象技を判定する
export const isDoublePowerMove = (move: MoveIdLike): boolean =>
  isDoublePowerMoveId(move?.id);

// じしんは名称でも判定できるようにする
export const isZishinMove = (move: MoveNameLike): boolean =>
  ZISHIN_MOVE_NAMES.has(move?.name_ja ?? '');

export const isElectroBallMove = (move: MoveNameLike): boolean =>
  ELECTRO_BALL_MOVE_NAMES.has(move?.name_ja ?? '');

export const isGyroBallMove = (move: MoveNameLike): boolean =>
  GYRO_BALL_MOVE_NAMES.has(move?.name_ja ?? '');

// めざめるダンスは名称でも判定できるようにする
export const isRevelationDanceMove = (move: MoveNameLike): boolean =>
  REVELATION_DANCE_MOVE_NAMES.has(move?.name_ja ?? '');

// テクノバスターは名称でも判定できるようにする
export const isTechnoBlastMove = (move: MoveNameLike): boolean =>
  TECHNO_BLAST_MOVE_NAMES.has(move?.name_ja ?? '');

// マルチアタックは名称でも判定できるようにする
export const isMultiAttackMove = (move: MoveNameLike): boolean =>
  MULTI_ATTACK_MOVE_NAMES.has(move?.name_ja ?? '');

export const isFreezeDryMove = (move: MoveNameLike): boolean =>
  FREEZE_DRY_MOVE_NAMES.has(move?.name_ja ?? '');

// ウェザーボールは名称でも判定できるようにする
export const isWeatherBallMove = (move: MoveNameLike): boolean =>
  WEATHER_BALL_MOVE_NAMES.has(move?.name_ja ?? '');

export const isBodyPressMove = (move: MoveNameLike): boolean =>
  BODY_PRESS_MOVE_NAMES.has(move?.name_ja ?? '');

// きしかいせい/じたばたは名称でも判定できるようにする
export const isKishikaiseiMove = (move: MoveNameLike): boolean =>
  KISHIKAISEI_MOVE_NAMES.has(move?.name_ja ?? '');

// マグニチュードは名称でも判定できるようにする
export const isMagnitudeMove = (move: MoveNameLike): boolean =>
  MAGNITUDE_MOVE_NAMES.has(move?.name_ja ?? '');

// イカサマは名称でも判定できるようにする
export const isIkasamaMove = (move: MoveNameLike): boolean =>
  IKASAMA_MOVE_NAMES.has(move?.name_ja ?? '');

// きる技は名称でも判定できるようにする
export const isKiruMove = (move: MoveNameLike): boolean =>
  KIRU_MOVE_NAMES.has(move?.name_ja ?? '');

// フライングプレスは名称でも判定できるようにする
export const isFlyingPressMove = (move: MoveNameLike): boolean =>
  FLYING_PRESS_MOVE_NAMES.has(move?.name_ja ?? '');

export const isStoredPowerMove = (move: MoveNameLike): boolean =>
  STORED_POWER_MOVE_NAMES.has(move?.name_ja ?? '');

// ライジングボルトは名称でも判定できるようにする
export const isRisingBoltMove = (move: MoveNameLike): boolean =>
  RISING_BOLT_MOVE_NAMES.has(move?.name_ja ?? '');

// ヘビーボンバーは名称でも判定できるようにする
export const isHeavySlamMove = (move: MoveNameLike): boolean =>
  HEAVY_SLAM_MOVE_NAMES.has(move?.name_ja ?? '');

// はたきおとすは名称でも判定できるようにする
export const isHatakiotosuMove = (move: MoveNameLike): boolean =>
  HATAKIOTOSU_MOVE_NAMES.has(move?.name_ja ?? '');

// おはかまいりは名称でも判定できるようにする
export const isOhakamairiMove = (move: MoveNameLike): boolean =>
  OHAKAMAIRI_MOVE_NAMES.has(move?.name_ja ?? '');

// 確定急所の技は名称でも判定できるようにする
export const isKakuteiCriticalMove = (move: MoveNameLike): boolean =>
  KAKUTEI_CRITICAL_MOVE_NAMES.has(move?.name_ja ?? '');

// かたやぶり技は名称でも判定できるようにする
export const isKataYaburiMove = (move: MoveNameLike): boolean =>
  KATAYABURI_MOVE_NAMES.has(move?.name_ja ?? '');

// ランク補正無視技は名称でも判定できるようにする
export const isThroughRankMove = (move: MoveNameLike): boolean =>
  THROUGH_RANK_MOVE_NAMES.has(move?.name_ja ?? '');

// フォトンゲイザーは名称でも判定できるようにする
export const isPhotonGeyserMove = (move: MoveNameLike): boolean =>
  PHOTON_GEYSER_MOVE_NAMES.has(move?.name_ja ?? '');

// サイコショック系技は名称でも判定できるようにする
export const isPsyshockMove = (move: MoveNameLike): boolean =>
  PSYSHOCK_MOVE_NAMES.has(move?.name_ja ?? '');

// すてみ技は名称でも判定できるようにする
export const isSutemiWazaMove = (move: MoveNameLike): boolean =>
  SUTEMI_WAZA_MOVE_NAMES.has(move?.name_ja ?? '');

// かみつき技は名称でも判定できるようにする
export const isKamitukiMove = (move: MoveNameLike): boolean =>
  KAMITUKI_MOVE_NAMES.has(move?.name_ja ?? '');

// 風技は名称でも判定できるようにする
export const isKazeMove = (move: MoveNameLike): boolean =>
  KAZE_MOVE_NAMES.has(move?.name_ja ?? '');

// 音技は名称でも判定できるようにする
export const isOtoMove = (move: MoveNameLike): boolean =>
  OTO_MOVE_NAMES.has(move?.name_ja ?? '');

// かべはかい技は名称・IDでも判定できるようにする
export const isKabehakaiMove = (move: MoveKabehakaiLike): boolean =>
  KABEHARAKAI_MOVE_NAMES.has(move?.name_ja ?? '') ||
  KABEHARAKAI_MOVE_IDS.has(move?.id ?? -1);

// けたぐりは名称でも判定できるようにする
export const isKetaguriMove = (move: MoveNameLike): boolean =>
  KETAGURI_MOVE_NAMES.has(move?.name_ja ?? '');

// ワイドフォースは名称でも判定できるようにする
export const isWideForceMove = (move: MoveNameLike): boolean =>
  WIDE_FORCE_MOVE_NAMES.has(move?.name_ja ?? '');

// サイコブレイドは名称でも判定できるようにする
export const isPsyBladeMove = (move: MoveNameLike): boolean =>
  PSY_BLADE_MOVE_NAMES.has(move?.name_ja ?? '');

// 弾技は名称でも判定できるようにする
export const isTamaMove = (move: MoveNameLike): boolean =>
  TAMA_MOVE_NAMES.has(move?.name_ja ?? '');

// トリプルキック/トリプルアクセルは名称でも判定できるようにする
export const isTripleKickAcceleratorMove = (move: MoveNameLike): boolean =>
  TRIPLE_KICK_ACCELERATOR_MOVE_NAMES.has(move?.name_ja ?? '');

export const isHadouMove = (move: MoveNameLike): boolean =>
  HADOU_MOVE_NAMES.has(move?.name_ja ?? '');

export const isWavesOfTheEarthMove = (move: MoveNameLike): boolean =>
  WAVES_OF_THE_EARTH_MOVE_NAMES.has(move?.name_ja ?? '');

export const isTerabrastMove = (move: MoveNameLike): boolean =>
  TERABRAST_MOVE_NAMES.has(move?.name_ja ?? '');

// テラス威力補正対象技を名称で判定する
export const isTerasPowerUpMove = (move: MoveNameLike): boolean =>
  TERAS_POWER_UP_MOVE_NAMES.has(move?.name_ja ?? '');

// シェルアームズは名称でも判定できるようにする
export const isShellSideArmMove = (move: MoveNameLike): boolean =>
  SHELL_SIDE_ARM_MOVE_NAMES.has(move?.name_ja ?? '');

export const isNamakeMove = (move: MoveNameLike): boolean =>
  NAMAKE_MOVE_NAMES.has(move?.name_ja ?? '');

// ハイドロスチームは名称でも判定できるようにする
export const isHydroSteamMove = (move: MoveNameLike): boolean =>
  HYDRO_STEAM_MOVE_NAMES.has(move?.name_ja ?? '');

export const isRenzokuMove = (move: MoveNameLike): boolean =>
  RENZOKU_MOVE_NAMES.has(move?.name_ja ?? '');

export const isMakibisiMove = (move: MoveNameLike): boolean =>
  MAKIBISI_MOVE_NAMES.has(move?.name_ja ?? '');

export const isHundonokobushiMove = (move: MoveNameLike): boolean =>
  HUNDONOKOBUSHI_MOVE_NAMES.has(move?.name_ja ?? '');

export const isPunchMove = (move: MoveNameLike): boolean =>
  PUNCH_MOVE_NAMES.has(move?.name_ja ?? '');

export const isTikarazukuTargetMove = (move: MoveNameLike): boolean =>
  TIKARAZUKU_TARGET_MOVE_NAMES.has(move?.name_ja ?? '');

// かたいツメ向けの直接攻撃判定
export const isDirectAttackMove = (move: MoveNameAndClassLike): boolean => {
  if (!move) {
    return false;
  }
  if (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL) {
    return !PHYSICAL_NON_DIRECT_MOVE_NAMES.has(move.name_ja ?? '');
  }
  if (move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL) {
    return !SPECIAL_NON_DIRECT_MOVE_NAMES.has(move.name_ja ?? '');
  }
  return false;
};
